import { useContext, useEffect, useMemo, useCallback } from "react";
// context
import { CartIPLSwiggySixesContext } from "@swiggy-private/core-ui";
// utils
import { getNestedValue, isEmpty } from "@swiggy-private/js-utils";
import { isValidNumber, getClosingTimeEpochFromStorage } from "@swiggy-private/core-ui";
import { getClosingTimeFromApiInSeconds } from "@cart-client/helpers/TimerOnCart";
// types
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { SwiggySixesCroutonAPIInfo, SwiggySixesCroutonComms, TimerUpdateSource } from "@swiggy-private/cart-types";

export const useIPLSwiggySixesCartCroutonAndUpdates = (cartResponse: ReviewedCart | null) => {
    const { validateAndSetTimestamp, croutonCommsState, tickerTimerInMinSec, timestampInMs } =
        useContext(CartIPLSwiggySixesContext);

    // extract timer from cart response
    const timerValueFromCartData: number | undefined = useMemo(() => {
        return getNestedValue(cartResponse, "data.iplSwiggySixesCroutonInfo.offerExpiresInSec");
    }, [cartResponse]);

    // pull out restaurant timer data for comparison
    const fetchRestaurantClosingTimeInSec = useCallback(() => {
        return getClosingTimeFromApiInSeconds(cartResponse);
    }, [cartResponse]);

    // pull out restaurant closing time from storage
    const fetchRestaurantCloseEpoch = useCallback(() => {
        return getClosingTimeEpochFromStorage();
    }, []);

    // Cases -
    // if IPL timer < Restaurant Timer = Allow IPL Timer
    // if Restaurant Timer >= IPL Timer = Allow Restaurant Timer
    // if after sometime another six is hit and IPL Timer becomes more than Restaurant Timer - re-evaluate and give Restaurant Timer priority if less
    const allowIPLCroutonOverRestaurantTimer = useMemo(() => {
        // IPL Ticker enabled and valid timestamp in storage
        if (
            tickerTimerInMinSec?.enabled &&
            !isEmpty(timestampInMs) &&
            isValidNumber(timestampInMs) &&
            timestampInMs > Date.now()
        ) {
            // NOTE - checking both closing duration and epoch in case local storage is not updated
            const restaurantCloseEpoch = fetchRestaurantCloseEpoch();
            const restaurantClosingTimeInSec = fetchRestaurantClosingTimeInSec();
            // CASE 1 - Restaurant close epoch is present and is valid in storage
            if (isValidNumber(restaurantCloseEpoch) && restaurantCloseEpoch > Date.now()) {
                return timestampInMs < restaurantCloseEpoch;
            }
            // CASE 2 - Restaurant closing time is present in response
            else if (isValidNumber(restaurantClosingTimeInSec) && restaurantClosingTimeInSec > 0) {
                const iplSecsLeft = Math.ceil((timestampInMs - Date.now()) / 1000);
                return iplSecsLeft < restaurantClosingTimeInSec;
            }
            // CASE 3 - Restaurant time is not present in storage or is invalid so enable IPL crouton
            return true;
        }
        // IPL Ticker not enabled or invalid timestamp in storage
        return false;
    }, [fetchRestaurantCloseEpoch, fetchRestaurantClosingTimeInSec, tickerTimerInMinSec?.enabled, timestampInMs]);

    const croutonData: SwiggySixesCroutonComms = useMemo(() => {
        const croutonComms: SwiggySixesCroutonAPIInfo["states"] | undefined = getNestedValue(
            cartResponse,
            "data.iplSwiggySixesCroutonInfo.states",
            undefined,
        );
        const icon: string = getNestedValue(cartResponse, "data.iplSwiggySixesCroutonInfo.icon", "");

        if (isEmpty(croutonComms?.[croutonCommsState]) || isEmpty(icon)) {
            return { enabled: false };
        }

        // tickerTime enabled key is the control for the entire project which decide if comms need to be shown or not
        // until the ticker is enabled all comms will be shown on crouton + savings corner
        return {
            enabled: tickerTimerInMinSec?.enabled && allowIPLCroutonOverRestaurantTimer,
            croutonComms: croutonComms?.[croutonCommsState] || [],
            croutonState: croutonCommsState,
            icon,
        };
    }, [allowIPLCroutonOverRestaurantTimer, cartResponse, croutonCommsState, tickerTimerInMinSec?.enabled]);

    // update the timer in store as the timerValueChanges
    useEffect(() => {
        if (!isEmpty(timerValueFromCartData) && isValidNumber(timerValueFromCartData)) {
            validateAndSetTimestamp?.(timerValueFromCartData, TimerUpdateSource.UPDATE_CART);
        }
    }, [timerValueFromCartData, validateAndSetTimestamp]);

    return {
        croutonData,
        allowIPLCroutonOverRestaurantTimer,
    };
};
